/**
 * Pure derivation that folds the `getDependencies` IPC's raw `DependencyData`
 * into the structured `DependencyAnnotations` surface consumed by the sidebar
 * + ColumnsTab. Lives in a sibling module from the hook so jsdom + node tests
 * can verify the classification without spinning up TanStack Query.
 *
 * Slice H2.
 */
import type {
  ColumnAnnotation,
  DependencyAnnotations,
  DependencyItem,
} from './types';

function classifyByType(items: readonly DependencyItem[]): {
  fk: number;
  view: number;
  routine: number;
  trigger: number;
  other: number;
} {
  let fk = 0;
  let view = 0;
  let routine = 0;
  let trigger = 0;
  let other = 0;
  for (const item of items) {
    switch (item.objectType) {
      case 'table':
        fk += 1;
        break;
      case 'view':
        view += 1;
        break;
      case 'trigger':
        trigger += 1;
        break;
      case 'procedure':
      case 'function':
        routine += 1;
        break;
      default:
        other += 1;
    }
  }
  return { fk, view, routine, trigger, other };
}

export interface BuildDependencyAnnotationsInput {
  readonly uses?: readonly DependencyItem[];
  readonly usedBy?: readonly DependencyItem[];
  readonly limitedSupport?: boolean;
}

export function buildDependencyAnnotations(
  input: BuildDependencyAnnotationsInput,
): DependencyAnnotations {
  const incoming = input.usedBy ?? [];
  const outgoing = input.uses ?? [];
  const incomingTotals = classifyByType(incoming);
  const outgoingTotals = classifyByType(outgoing);

  const perColumn = derivePerColumnFromIpc(incoming);

  return {
    summary: {
      incomingFkCount: incomingTotals.fk,
      outgoingFkCount: outgoingTotals.fk,
      dependentViewCount: incomingTotals.view,
      dependentRoutineCount: incomingTotals.routine,
      dependentTriggerCount: incomingTotals.trigger,
      otherUsedByCount: incomingTotals.other,
      totalIncoming: incoming.length,
      totalOutgoing: outgoing.length,
    },
    incoming,
    outgoing,
    perColumn,
    limitedSupport: input.limitedSupport === true,
  };
}

function derivePerColumnFromIpc(
  incoming: readonly DependencyItem[],
): ReadonlyMap<string, ColumnAnnotation> {
  const map = new Map<string, { fks: DependencyItem[]; views: DependencyItem[] }>();
  for (const item of incoming) {
    if (!item.columnName) continue;
    const key = item.columnName.toLowerCase();
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { fks: [], views: [] };
      map.set(key, bucket);
    }
    if (item.objectType === 'table') bucket.fks.push(item);
    else if (item.objectType === 'view') bucket.views.push(item);
  }
  const out = new Map<string, ColumnAnnotation>();
  for (const [key, bucket] of map) {
    out.set(key, {
      sequenceDefault: null,
      incomingFkSources: bucket.fks,
      viewProjections: bucket.views,
    });
  }
  return out;
}

/** Lookup the per-column annotation for a given column name; returns an empty
 *  annotation when the IPC didn't attribute any dependent to this column. */
export function getColumnAnnotation(
  annotations: DependencyAnnotations | null | undefined,
  columnName: string,
): ColumnAnnotation {
  const empty: ColumnAnnotation = {
    sequenceDefault: null,
    incomingFkSources: [],
    viewProjections: [],
  };
  if (!annotations) return empty;
  return annotations.perColumn.get(columnName.toLowerCase()) ?? empty;
}

/** Empty `DependencyAnnotations` shape — used as the initial / disconnected /
 *  error fallback. */
export function emptyDependencyAnnotations(): DependencyAnnotations {
  return {
    summary: {
      incomingFkCount: 0,
      outgoingFkCount: 0,
      dependentViewCount: 0,
      dependentRoutineCount: 0,
      dependentTriggerCount: 0,
      otherUsedByCount: 0,
      totalIncoming: 0,
      totalOutgoing: 0,
    },
    incoming: [],
    outgoing: [],
    perColumn: new Map(),
    limitedSupport: false,
  };
}
